import { date } from 'quasar'
const { formatDate } = date
import Model, { ID, Integer, IsoDate, IsoDateTime, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import Section from 'src/models/page/Section'
import { deletePrefix } from 'src/router/utils'

export default class Page extends Model {
  static name = 'Page'

  static paginate = true
  static deletable = true
  static duplicatable = true
  static versioned = true
  static publishable = true
  static isConfiguration = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.SiteContent]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      draftId: {
        type: ID,
        output: true
      },
      draft: {
        type: Page,
        output: true
      },
      draftParent: {
        type: Page,
        output: true
      },
      title: {
        type: String,
        required: true,
        translatable: true
      },
      slug: {
        type: String,
        output: true
        // TODO INDY-3193: this is intentionally excluded for now as we haven't updated
        // the vue-router to support translated slugs
        // translatable: true
      },
      isVisibleToSearchEngines: {
        type: Boolean,
        default: true
      },
      searchEngineSnippet: {
        type: String,
        default: '',
        excludeFromDefault: true,
        translatable: true
      },
      sections: {
        type: [Section],
        default: () => ([]),
        encode (data) {
          data.sectionIds = _.map(this.sections.data, 'id')
        },
        reference: true
      },
      published: {
        type: Boolean,
        default: false
      },
      publishedAt: {
        type: IsoDateTime,
        output: true
      },
      startDate: {
        type: IsoDate,
        default: null
      },
      endDate: {
        type: IsoDate,
        default: null
      },
      viewsPastNinetyDays: {
        type: Integer,
        output: true,
        excludeFromDefault: true
      },
      describeChanges: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      displayOrder: {
        type: Integer,
        required: true,
        default: 100
      },
      scopeClass: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      styles: {
        type: String,
        default: '',
        excludeFromDefault: true
      },
      transformedStyles: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      pageTransformedStyles: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      action: {
        type: String,
        default: null,
        input: true
      }
    }

    this.computed = {
      current () {
        const date = this.configuration.currentSite.siteDate

        return (!this.startDate || date >= this.startDate.toString()) && (!this.endDate || date <= this.endDate.toString())
      },
      visible () {
        return this.published && this.isUnarchived && this.current
      },
      defaultHomePage () {
        // First visible page is the homepage unless it has been explicitly set
        return _.find(this.configuration.publishedPages.data, 'visible')
      },
      webHomePage () {
        if (this.configuration?.currentSite?.webHomePage?.visible) {
          return this.configuration.currentSite.webHomePage
        }

        return this.defaultHomePage
      },
      appHomePage () {
        if (this.configuration?.currentSite?.appHomePage?.visible) {
          return this.configuration.currentSite.appHomePage
        }

        return this.defaultHomePage
      },
      homePage () {
        if (window._capacitorNative) {
          return this.appHomePage
        }

        return this.webHomePage
      },
      isHomePage () {
        return this.homePage?.id && this.homePage.id === this.id
      }
    }

    this.queries = {
      previewPage: {
        arguments: {
          id: {
            type: ID,
            required: true
          }
        },
        results: {
          page: Page
        }
      }
    }
  }

  static get configurationCollectionVariableName () {
    return 'publishedPages'
  }

  get name () {
    return this.title
  }

  get route () {
    return this.slug ? `/${deletePrefix(this.slug, '/')}` : '/'
  }

  get deletionWarningText () {
    if (this.configuration.navigations && _.includes(_.map(this.configuration.navigations.data, navigation => navigation?.page?.id), this.id)) {
      return 'Any page navigations will subsequently be deleted'
    }

    return null
  }

  get displayIcons () {
    const icons = []

    if (!this.published) {
      icons.push({
        name: 'toggle_off',
        color: 'grey',
        tooltip: 'Not Published',
        size: 'sm'
      })
      return icons
    }

    if (this.isHomePage) {
      icons.push({
        name: 'home',
        color: 'positive',
        tooltip: 'Home Page',
        size: 'sm'
      })
    } else if (this.visible && !this.startDate && !this.endDate) {
      icons.push({
        name: 'toggle_on',
        color: 'positive',
        tooltip: 'Published',
        size: 'sm'
      })
    } else if (this.visible) {
      icons.push({
        name: 'visibility',
        color: 'positive',
        tooltip: 'Visible Now',
        size: 'sm'
      })
    } else {
      icons.push({
        name: 'visibility_off',
        color: 'negative',
        tooltip: 'Not Visible',
        size: 'sm'
      })
    }

    if (this.startDate || this.endDate) {
      const now = this.configuration.currentSite.siteTime
      const startDate = this.startDate && this.startDate.inTimeZone(this.configuration.currentSite.timeZone)
      const endDate = this.endDate && this.endDate.inTimeZone(this.configuration.currentSite.timeZone)

      if (startDate && now <= startDate) {
        icons.push({
          name: 'schedule',
          color: 'positive',
          tooltip: `Starts on ${formatDate(startDate, 'MMM D, YYYY')} (${startDate.relativeTime()})`,
          size: 'sm'
        })
      } else if (endDate && now <= endDate) {
        icons.push({
          name: 'schedule',
          color: 'negative',
          tooltip: `Ends on ${formatDate(endDate, 'MMM D, YYYY')} (${endDate.relativeTime()})`,
          size: 'sm'
        })
      } else if (endDate && now > endDate) {
        icons.push({
          name: 'schedule',
          color: 'grey',
          tooltip: `Ended on ${formatDate(endDate, 'MMM D, YYYY')} (${endDate.relativeTime()})`,
          size: 'sm'
        })
      }
    }

    if (this.mobile) {
      icons.push({
        name: 'smartphone',
        color: 'dark',
        tooltip: 'Visible on Mobile',
        size: 'sm'
      })
    }

    if (this.desktop) {
      icons.push({
        name: 'computer',
        color: 'dark',
        tooltip: 'Visible on Desktop',
        size: 'sm'
      })
    }

    return icons
  }

  async publish () {
    this.action = 'publish'

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async unpublish () {
    this.action = 'unpublish'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async discardDraft () {
    this.action = 'discard_draft'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
